/**
 * `/agents/:agentId/settings` — Spec #151 Phase 3-B.
 *
 * Phase 3-B 以前の Agent 詳細ページ (settings / trust / memory) を、
 * Agent Lucid (`/agents/:agentId`) からスピンオフした power-user
 * 用の設定ページ。Phase 3-B では Agent Lucid を主役にしたいので、これら
 * 詳細パネルは「設定」リンクからのみ到達する。
 *
 * 振る舞いは Phase 3a 時点の `agents.$agentId.tsx` を踏襲。
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Settings as SettingsIcon } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { AuthGate } from '@/components/AuthGate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AgentForm } from '@/components/agents/AgentForm';
import { DeleteAgentDialog } from '@/components/agents/DeleteAgentDialog';
import { MemoryPanel } from '@/components/agents/MemoryPanel';
import { RepositoriesPanel } from '@/components/agents/RepositoriesPanel';
import { MailAccountsPanel } from '@/components/agents/MailAccountsPanel';
import { LedgerPanel } from '@/components/agents/LedgerPanel';
import { useAgent, useUpdateAgent } from '@/hooks/useAgent';

function AgentSettingsView({ agentId }: { agentId: string }) {
  const { data: agent, isLoading, error } = useAgent(agentId);
  const update = useUpdateAgent(agentId);
  const navigate = useNavigate();

  return (
    <AppShell>
      <div className="p-6 max-w-3xl mx-auto flex flex-col gap-4">
        <Link
          to="/agents/$agentId"
          params={{ agentId }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground w-fit"
        >
          <ArrowLeft className="size-4" />
          Lucid に戻る
        </Link>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">読み込み中...</p>
        ) : error ? (
          <p className="text-sm text-destructive">
            Agent を読み込めませんでした ({String(error)})
          </p>
        ) : !agent ? (
          <p className="text-sm text-destructive">Agent が見つかりません</p>
        ) : (
          <>
            <header className="flex items-center gap-3">
              <SettingsIcon className="size-5 text-accent" aria-hidden />
              <div className="flex-1 min-w-0">
                <h1
                  data-testid="agent-settings-name"
                  className="text-2xl font-semibold truncate"
                >
                  {agent.name} の設定
                </h1>
                <p className="text-xs text-muted-foreground">
                  {agent.llmProvider} / {agent.llmModel}
                </p>
              </div>
              <DeleteAgentDialog
                agentId={agent.id}
                agentName={agent.name}
                onDeleted={() => {
                  // D-5: /agents リスト画面は削除済み。削除後はホームへ。
                  void navigate({ to: '/lucid' });
                }}
              />
            </header>

            <Card>
              <CardHeader>
                <CardTitle>設定</CardTitle>
                <CardDescription>名前 / LLM プロバイダ・モデルの編集</CardDescription>
              </CardHeader>
              <CardContent>
                <AgentForm
                  initial={{
                    name: agent.name,
                    llmProvider: agent.llmProvider,
                    llmModel: agent.llmModel,
                    // Spec #169: API returns a normalized `enabledTools`.
                    enabledTools: agent.enabledTools,
                  }}
                  submitLabel="保存"
                  pending={update.isPending}
                  errorMessage={
                    update.error ? `保存に失敗しました (${String(update.error)})` : null
                  }
                  onSubmit={async (value) => {
                    try {
                      // Spec #169: forward the explicit tool selection so
                      // the PATCH replaces `enabled_tools`.
                      await update.mutateAsync(value);
                    } catch (err) {
                      console.error('[update-agent] failed', err);
                    }
                  }}
                />
              </CardContent>
            </Card>

            <LedgerPanel agentId={agent.id} />
            <RepositoriesPanel agentId={agent.id} />
            <MailAccountsPanel agentId={agent.id} />
            <MemoryPanel agentId={agent.id} />
          </>
        )}
      </div>
    </AppShell>
  );
}

function GatedSettings() {
  const { agentId } = Route.useParams();
  return (
    <AuthGate>
      <AgentSettingsView agentId={agentId} />
    </AuthGate>
  );
}

export const Route = createFileRoute('/agents/$agentId/settings')({
  component: GatedSettings,
});
